import { clearHumioSessionCache } from '@investigator/features/investigations/utils';
import { useCallback, useContext } from 'react';
import { safeParse } from 'secure-json-parse';
import {
  AuthContext,
  clearUserSpecificData,
  getUserId,
  getUserScopedKey,
} from '../../providers';
import { CognitoUserInterface } from '../../types/auth-types';

/**
 * Interface for token validation result
 */
interface TokenValidationResult {
  isValid: boolean;
  token: string | null;
  expiresAt?: number;
}

// Token validation constants
const TOKEN_MIN_LENGTH = 20;
const TOKEN_EXPIRY_BUFFER_SEC = 60;

/**
 * Custom hook for managing authentication tokens
 * Handles storage, retrieval, and cleanup of Cognito ID tokens
 * Note: Humio tokens are managed exclusively by HumioTokenManager
 *
 * @returns {Object} Token management functions
 */
export const useTokenManagement = () => {
  const { user } = useContext(AuthContext);

  /**
   * Validates a JWT token structure and expiration
   * @param {string | null} token - Token to validate
   * @returns {TokenValidationResult} Validation result
   */
  const validateToken = useCallback(
    (token: string | null): TokenValidationResult => {
      if (
        !token ||
        token === 'undefined' ||
        token === 'null' ||
        token.length < TOKEN_MIN_LENGTH
      ) {
        return { isValid: false, token: null };
      }

      try {
        // Basic JWT structure validation
        const parts = token.split('.');
        if (parts.length !== 3) {
          return { isValid: false, token: null };
        }

        // Decode and check expiration
        const payloadB64 = parts[1];

        const payload = safeParse(
          atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')),
        ) as { exp?: number };

        const exp = payload.exp;
        if (!exp) {
          return { isValid: false, token: null };
        }

        const expiresAt = exp * 1000; // Convert to milliseconds
        const now = Date.now();
        const bufferMs = TOKEN_EXPIRY_BUFFER_SEC * 1000;

        if (expiresAt - now <= bufferMs) {
          return { isValid: false, token, expiresAt };
        }

        return { isValid: true, token, expiresAt };
      } catch (error) {
        console.warn('[TokenManagement] Token validation failed:', error);
        return { isValid: false, token: null };
      }
    },
    [],
  );

  /**
   * Saves Cognito ID token to localStorage with user scoping
   * @param {string} token - Cognito ID token to save
   * @param {CognitoUserInterface} [targetUser] - Optional user for scoping
   */
  const saveIdToken = useCallback(
    (token: string, targetUser?: CognitoUserInterface) => {
      if (!token || token === 'undefined' || token === 'null') {
        console.warn('[TokenManagement] Attempted to save invalid token');
        return;
      }

      const userToUse = targetUser || user;
      const userId = getUserId(userToUse);

      // Validate token before saving
      const validation = validateToken(token);
      if (!validation.isValid) {
        console.warn('[TokenManagement] Token validation failed, not saving');
        return;
      }

      try {
        // Save user-scoped token
        if (userId) {
          const userKey = getUserScopedKey('docs_id_token', userId);
          localStorage.setItem(userKey, token);
        }

        // Always save to legacy key for backward compatibility
        localStorage.setItem('docs_id_token', token);
      } catch (error) {
        console.error('[TokenManagement] Failed to save ID token:', error);
      }
    },
    [user, validateToken],
  );

  /**
   * Clears all tokens for a user or globally
   * Note: This clears both Cognito and Humio tokens
   * @param {CognitoUserInterface} [targetUser] - Optional user for targeted clearing
   */
  const clearTokens = useCallback(
    (targetUser?: CognitoUserInterface) => {
      const userToUse = targetUser || user;
      const userId = getUserId(userToUse);

      try {
        if (userId) {
          // Clear user-specific data
          clearUserSpecificData(userId);

          // Clear user-specific ID token
          localStorage.removeItem(getUserScopedKey('docs_id_token', userId));

          // Clear Humio tokens via HumioTokenManager
          clearHumioSessionCache(userId);
        } else {
          // Clear all user data
          clearUserSpecificData();

          // Clear legacy ID token
          localStorage.removeItem('docs_id_token');

          // Clear all Humio tokens
          clearHumioSessionCache();
        }
      } catch (error) {
        console.error('[TokenManagement] Error clearing tokens:', error);
      }
    },
    [user],
  );

  /**
   * Retrieves stored Cognito ID token with validation
   * @param {CognitoUserInterface} [targetUser] - Optional user for scoped retrieval
   * @returns {string | null} Valid token or null
   */
  const getStoredIdToken = useCallback(
    (targetUser?: CognitoUserInterface): string | null => {
      const userToUse = targetUser || user;
      const userId = getUserId(userToUse);

      try {
        let token: string | null = null;

        // Try user-scoped key first
        if (userId) {
          const userKey = getUserScopedKey('docs_id_token', userId);
          token = localStorage.getItem(userKey);
        }

        // Fallback to legacy key
        if (!token) {
          token = localStorage.getItem('docs_id_token');
        }

        // Validate token before returning
        const validation = validateToken(token);
        if (!validation.isValid) {
          return null;
        }

        return validation.token;
      } catch (error) {
        console.error('[TokenManagement] Error retrieving ID token:', error);
        return null;
      }
    },
    [user, validateToken],
  );

  /**
   * Retrieves stored Humio token (read-only)
   * Note: Humio tokens should only be created via setHumioSessionId
   * @param {CognitoUserInterface} [targetUser] - Optional user for scoped retrieval
   * @returns {string | null} Stored Humio token or null
   */
  const getStoredHumioToken = useCallback(
    (targetUser?: CognitoUserInterface): string | null => {
      const userToUse = targetUser || user;
      const userId = getUserId(userToUse);

      try {
        let token: string | null = null;

        // Try user-scoped key first
        if (userId) {
          const userKey = getUserScopedKey('session_id_token', userId);
          token = localStorage.getItem(userKey);
        }

        // Fallback to legacy key
        if (!token) {
          token = localStorage.getItem('session_id_token');
        }

        // Basic validation
        if (!token || token === 'undefined' || token === 'null') {
          return null;
        }

        return token;
      } catch (error) {
        console.error('[TokenManagement] Error retrieving Humio token:', error);
        return null;
      }
    },
    [user],
  );

  /**
   * Checks if stored tokens are valid and not expired
   * @returns {Object} Validation status for both token types
   */
  const checkTokensHealth = useCallback((): {
    idTokenValid: boolean;
    humioTokenExists: boolean;
  } => {
    const idToken = getStoredIdToken();
    const humioToken = getStoredHumioToken();

    return {
      idTokenValid: !!idToken,
      humioTokenExists: !!humioToken,
    };
  }, [getStoredIdToken, getStoredHumioToken]);

  return {
    // Core functions
    saveIdToken, // Save Cognito ID token
    clearTokens, // Clear all tokens
    getStoredIdToken, // Get valid Cognito ID token
    getStoredHumioToken, // Get Humio token (read-only)

    // Utilities
    validateToken, // Validate token structure and expiration
    checkTokensHealth, // Check overall token health

    // Note: No saveHumioToken - Humio tokens must be created via setHumioSessionId
  };
};
